import { Controller } from "@hotwired/stimulus"
import { stream } from "@durable-streams/client"
import { marked } from "marked"

export default class extends Controller {
  static targets = [
    "promptInput", "generateButton", "resultContent", "resultSection",
    "savePromptButton", "fileBadge", "rawMarkdown", "copyButton", "copyIcon", "checkIcon",
    // Studio agent targets
    "botSelect", "botSelectPlaceholder", "fetchBotsButton", "fetchBotsLabel",
    "studioGenerateButton",
    // Mode switching targets
    "standardControls", "studioControls", "studioAgentPanel",
    "standardModeBtn", "studioModeBtn"
  ]
  static values = {
    activityId: String,
    inloopId: String,
    streamUrl: String,
    savePromptUrl: String,
    generateUrl: String,
    hasAnalysisSources: { type: Boolean, default: false },
    hasFiles: { type: Boolean, default: false },
    fileCount: { type: Number, default: 0 },
    // Studio agent values
    fetchBotsUrl: String,
    saveAgentConfigUrl: String,
    runStudioUrl: String,
    mode: { type: String, default: "standard" }
  }

  connect() {
    this.activeStream = null
    this.selectedOutcomeCount = this.hasAnalysisSourcesValue && !this.hasFilesValue ? 1 : 0
    this._onFilesChanged = this.handleFilesChanged.bind(this)
    window.addEventListener("analytic-files-changed", this._onFilesChanged)
    this.updateAnalysisSourceUI()

    // Reconnect to active stream if any (page refresh recovery — Studio flow)
    if (this.streamUrlValue) {
      this.connectToStream(this.streamUrlValue)
    }
  }

  // Called by Stimulus whenever a resultContent target is connected (including via Turbo Stream updates)
  resultContentTargetConnected(element) {
    const text = element.textContent.trim()
    if (text) {
      element.innerHTML = marked.parse(text)
    }
  }

  // Called by Stimulus whenever a resultSection target is connected (including via Turbo Stream updates)
  resultSectionTargetConnected(element) {
    const state = element.dataset.state
    if (state === "completed") {
      this.resetGenerateButton("regenerate")
      this.resetStudioButton("rerun")
    } else if (state === "error") {
      this.resetGenerateButton("retry")
      this.resetStudioButton("retry")
    }
  }

  disconnect() {
    window.removeEventListener("analytic-files-changed", this._onFilesChanged)
    if (this.activeStream) {
      this.activeStream.abort()
      this.activeStream = null
    }
  }

  handleFilesChanged(event) {
    const count = event.detail.count || 0
    this.hasFilesValue = count > 0
    this.fileCountValue = count
    this.selectedOutcomeCount = event.detail.selectedOutcomeCount || 0
    this.updateAnalysisSourceUI()
  }

  updateAnalysisSourceUI() {
    const fileCount = this.fileCountValue
    const hasPreviousOutcomeContext = this.selectedOutcomeCount > 0
    const hasAnySource = fileCount > 0 || hasPreviousOutcomeContext
    this.hasAnalysisSourcesValue = hasAnySource

    if (this.hasFileBadgeTarget) {
      if (fileCount > 0) {
        this.fileBadgeTarget.className = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-brand-50 text-brand-700"
        this.fileBadgeTarget.innerHTML = `
          <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>
          ${fileCount} file${fileCount > 1 ? "s" : ""} uploaded
        `
      } else if (hasPreviousOutcomeContext) {
        this.fileBadgeTarget.className = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-brand-50 text-brand-700"
        this.fileBadgeTarget.innerHTML = `
          <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>
          Using selected previous activity outcomes
        `
      } else {
        this.fileBadgeTarget.className = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
        this.fileBadgeTarget.innerHTML = `
          <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          No analysis sources selected
        `
      }
    }

    if (this.hasGenerateButtonTarget) {
      if (!this.buttonIsBusy(this.generateButtonTarget)) {
        this.generateButtonTarget.disabled = !hasAnySource
      }
    }

    if (this.hasStudioGenerateButtonTarget && this.hasBotSelectTarget) {
      if (!this.buttonIsBusy(this.studioGenerateButtonTarget)) {
        this.studioGenerateButtonTarget.disabled = !hasAnySource || !this.botSelectTarget.value
      }
    }
  }

  buttonIsBusy(button) {
    const text = button?.textContent || ""
    return /Analyzing|Delegating|Sending/.test(text)
  }

  async savePrompt() {
    if (!this.hasPromptInputTarget) return

    const prompt = this.promptInputTarget.value.trim()

    if (this.hasSavePromptButtonTarget) {
      this.savePromptButtonTarget.disabled = true
    }

    try {
      const response = await fetch(this.savePromptUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ analysis_prompt: prompt })
      })

      if (response.ok) {
        if (this.hasSavePromptButtonTarget) {
          const btn = this.savePromptButtonTarget
          const originalHTML = btn.innerHTML
          btn.innerHTML = `<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg> Saved`
          setTimeout(() => {
            btn.disabled = false
            btn.innerHTML = originalHTML
          }, 2000)
        }
      } else {
        if (this.hasSavePromptButtonTarget) {
          this.savePromptButtonTarget.disabled = false
        }
      }
    } catch (error) {
      console.error("Failed to save analysis prompt:", error)
      if (this.hasSavePromptButtonTarget) {
        this.savePromptButtonTarget.disabled = false
      }
    }
  }

  async generate() {
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = true
      this.generateButtonTarget.innerHTML = `
        <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        Analyzing...
      `
    }

    if (this.hasPromptInputTarget && this.savePromptUrlValue) {
      await fetch(this.savePromptUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ analysis_prompt: this.promptInputTarget.value.trim() })
      })
    }

    try {
      const response = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (response.ok) {
        this.showGeneratingState("Analyzing your files...")
        // Progress updates and completion are handled via Turbo Streams (ActionCable)
      } else {
        const errorData = await response.json()
        this.showError(errorData.error || "Failed to start analysis")
        this.resetGenerateButton()
      }
    } catch (error) {
      console.error("Failed to generate analysis:", error)
      this.showError("Failed to start analysis")
      this.resetGenerateButton()
    }
  }

  // ─── Mode switching ──────────────────────────────────────────────────────

  modeValueChanged(mode) {
    const isStudio = mode === "studio"

    if (this.hasStandardControlsTarget) {
      this.standardControlsTarget.style.display = isStudio ? "none" : ""
    }
    if (this.hasStudioControlsTarget) {
      this.studioControlsTarget.style.display = isStudio ? "" : "none"
    }
    if (this.hasStudioAgentPanelTarget) {
      this.studioAgentPanelTarget.style.display = isStudio ? "" : "none"
    }
    if (this.hasStandardModeBtnTarget) {
      this.standardModeBtnTarget.className = isStudio
        ? "px-2.5 py-1 rounded-md text-xs font-medium transition-colors text-gray-500 hover:text-gray-700"
        : "px-2.5 py-1 rounded-md text-xs font-medium transition-colors bg-white shadow-sm text-gray-900"
    }
    if (this.hasStudioModeBtnTarget) {
      this.studioModeBtnTarget.className = isStudio
        ? "flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors bg-white shadow-sm text-gray-900"
        : "flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors text-gray-500 hover:text-gray-700"
    }
  }

  switchToStandard() {
    this.modeValue = "standard"
  }

  switchToStudio() {
    this.modeValue = "studio"
  }

  // ─── Studio Agent actions ────────────────────────────────────────────────

  async fetchStudioBots() {
    if (!this.hasFetchBotsUrlValue) return

    const btn = this.hasFetchBotsButtonTarget ? this.fetchBotsButtonTarget : null
    const label = this.hasFetchBotsLabelTarget ? this.fetchBotsLabelTarget : null

    if (btn) btn.disabled = true
    if (label) label.textContent = "Loading..."

    try {
      const response = await fetch(this.fetchBotsUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (response.ok) {
        if (label) label.textContent = "Requested!"
        setTimeout(() => {
          if (label) label.textContent = "Refresh"
          if (btn) btn.disabled = false
        }, 3000)
        // The bot list will arrive asynchronously via RunwayBridge → page reload or Turbo broadcast
        this.showBotFetchNotice()
      } else {
        const data = await response.json()
        alert(data.error || "Failed to request bot list from Studio.")
        if (label) label.textContent = "Load Bots"
        if (btn) btn.disabled = false
      }
    } catch (error) {
      console.error("fetchStudioBots error:", error)
      if (label) label.textContent = "Load Bots"
      if (btn) btn.disabled = false
    }
  }

  showBotFetchNotice() {
    if (!this.hasBotSelectPlaceholderTarget) return
    this.botSelectPlaceholderTarget.innerHTML = `
      <p class="text-xs text-brand-600 text-center">
        <svg class="inline w-3 h-3 animate-spin mr-1" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        Bot list requested from Studio. The page will refresh when ready.
      </p>
    `
    // Poll for bot list availability by refreshing after a short delay
    setTimeout(() => { window.location.reload() }, 4000)
  }

  onBotSelected() {
    if (!this.hasBotSelectTarget) return

    const select = this.botSelectTarget
    const botId = select.value
    const option = select.options[select.selectedIndex]
    const botName = option ? (option.dataset.name || option.text) : ""

    if (!botId || !this.hasSaveAgentConfigUrlValue) return

    // Enable the run button
    if (this.hasStudioGenerateButtonTarget) {
      this.studioGenerateButtonTarget.disabled = !this.hasAnalysisSourcesValue
    }

    // Auto-save selection
    fetch(this.saveAgentConfigUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({ selected_bot_id: botId, selected_bot_name: botName })
    }).catch(err => console.error("Failed to save bot selection:", err))
  }

  async runStudioAnalysis() {
    if (!this.hasRunStudioUrlValue) return

    const btn = this.hasStudioGenerateButtonTarget ? this.studioGenerateButtonTarget : null
    if (btn) {
      btn.disabled = true
      btn.innerHTML = `
        <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        Delegating...
      `
    }

    try {
      const response = await fetch(this.runStudioUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (response.ok) {
        const data = await response.json()
        const agentName = data.bot_name || "Studio Agent"
        this.showGeneratingState(`Sending to ${agentName}...`, "This may take a moment while the agent processes your files.")
        if (data.stream_url) {
          this.connectToStream(data.stream_url)
        }
      } else {
        const errorData = await response.json()
        this.showError(errorData.error || "Failed to start Studio analysis")
        if (btn) {
          btn.disabled = false
          btn.innerHTML = `<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h10M12 2v10"/></svg> Run with Agent`
        }
      }
    } catch (error) {
      console.error("runStudioAnalysis error:", error)
      this.showError("Failed to start Studio analysis")
      if (btn) btn.disabled = false
    }
  }

  // ─── Stream handling ────────────────────────────────────────────────────

  async connectToStream(streamUrl) {
    if (this.activeStream) {
      this.activeStream.abort()
    }

    const abortController = new AbortController()
    this.activeStream = abortController

    try {
      const res = await stream({
        url: streamUrl,
        offset: "-1",
        live: "sse",
        json: true,
        signal: abortController.signal
      })

      res.subscribeJson(async (batch) => {
        for (const item of batch.items) {
          this.handleStreamEvent(item)
        }
      })

      res.closed.then(() => {
        this.activeStream = null
      })
    } catch (error) {
      if (error.name === "AbortError") return
      console.error("[ai-analytic-output] Stream error:", error)
      this.activeStream = null
    }
  }

  handleStreamEvent(event) {
    switch (event.type) {
      case "progress":
        this.showProgressState(event)
        break
      case "completed":
        this.showContent(event.content)
        this.resetGenerateButton("regenerate")
        this.resetStudioButton("rerun")
        break
      case "error":
        this.showError(event.error || "Analysis failed")
        this.resetGenerateButton("retry")
        this.resetStudioButton("retry")
        break
    }
  }

  // ─── UI helpers ────────────────────────────────────────────────────────

  showProgressState(event) {
    if (!this.hasResultSectionTarget) return

    const percent = event.percent ?? Math.round((event.step / event.total_steps) * 100)
    const message = event.message || "Processing..."
    const stepLabel = `Step ${event.step} of ${event.total_steps}`

    this.resultSectionTarget.innerHTML = `
      <div class="p-4">
        <div class="flex flex-col items-center gap-5">
          <div class="w-11 h-11 rounded-full bg-brand-50 flex items-center justify-center">
            <svg class="w-5 h-5 text-brand-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
          </div>
          <div class="flex flex-col items-center gap-1 text-center">
            <p class="text-sm font-semibold text-gray-800">${message}</p>
            <span class="text-xs text-gray-400">${stepLabel}</span>
          </div>
          <div class="w-full max-w-xs flex flex-col gap-1.5">
            <div class="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div class="bg-brand-500 h-1.5 rounded-full transition-all duration-500" style="width: ${percent}%"></div>
            </div>
            <p class="text-xs text-gray-400 text-right">${percent}%</p>
          </div>
        </div>
      </div>
    `
  }

  showGeneratingState(message = "Analyzing your files...", subMessage = "") {
    if (this.hasResultSectionTarget) {
      this.resultSectionTarget.innerHTML = `
        <div class="p-4">
          <div class="flex flex-col items-center gap-5">
            <div class="w-11 h-11 rounded-full bg-brand-50 flex items-center justify-center">
              <svg class="w-5 h-5 text-brand-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            </div>
            <div class="flex flex-col items-center gap-1 text-center">
              <p class="text-sm font-semibold text-gray-800">${message}</p>
              ${subMessage ? `<p class="text-xs text-gray-400">${subMessage}</p>` : ""}
            </div>
          </div>
        </div>
      `
    }
  }

  showContent(content) {
    if (this.hasResultSectionTarget) {
      this.resultSectionTarget.innerHTML = `
        <div class="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div class="p-4 relative">
            <button type="button"
                    class="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    data-action="click->ai-analytic-output#copyMarkdown"
                    data-ai-analytic-output-target="copyButton"
                    title="Copy as Markdown">
              <span data-ai-analytic-output-target="copyIcon">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                </svg>
              </span>
              <span class="hidden" data-ai-analytic-output-target="checkIcon">
                <svg class="w-4 h-4 text-success-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
              </span>
            </button>
            <div class="prose prose-sm max-w-none text-gray-700 pr-8" data-ai-analytic-output-target="resultContent">${marked.parse(content)}</div>
            <textarea class="hidden" data-ai-analytic-output-target="rawMarkdown">${this.escapeHtml(content)}</textarea>
            <div class="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <span class="text-xs text-gray-400">Just generated</span>
            </div>
          </div>
        </div>
      `
    }
  }

  copyMarkdown() {
    const raw = this.hasRawMarkdownTarget ? this.rawMarkdownTarget.value : null
    if (!raw) return

    navigator.clipboard.writeText(raw).then(() => {
      if (this.hasCopyIconTarget && this.hasCheckIconTarget) {
        const copyIcon = this.copyIconTargets[this.copyIconTargets.length - 1]
        const checkIcon = this.checkIconTargets[this.checkIconTargets.length - 1]
        copyIcon.classList.add("hidden")
        checkIcon.classList.remove("hidden")
        setTimeout(() => {
          copyIcon.classList.remove("hidden")
          checkIcon.classList.add("hidden")
        }, 2000)
      }
    }).catch((err) => {
      console.error("Failed to copy markdown:", err)
    })
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  showError(message) {
    if (this.hasResultSectionTarget) {
      this.resultSectionTarget.innerHTML = `
        <div class="bg-white border border-error-200 rounded-xl p-4">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-full bg-error-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-error-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <p class="text-sm text-error-700">${message}</p>
          </div>
        </div>
      `
    }
  }

  resetGenerateButton(mode = "initial") {
    if (!this.hasGenerateButtonTarget) return
    this.generateButtonTarget.disabled = !this.hasAnalysisSourcesValue

    if (mode === "regenerate") {
      this.generateButtonTarget.innerHTML = `
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
        </svg>
        Regenerate
      `
    } else if (mode === "retry") {
      this.generateButtonTarget.innerHTML = "Retry"
    } else {
      this.generateButtonTarget.innerHTML = "Run Analysis"
    }
  }

  resetStudioButton(mode = "initial") {
    if (!this.hasStudioGenerateButtonTarget) return
    const botSelected = this.hasBotSelectTarget ? this.botSelectTarget.value : true
    this.studioGenerateButtonTarget.disabled = !this.hasAnalysisSourcesValue || !botSelected

    if (mode === "rerun") {
      this.studioGenerateButtonTarget.innerHTML = `
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
        </svg>
        Re-run with Agent
      `
    } else if (mode === "retry") {
      this.studioGenerateButtonTarget.innerHTML = "Retry"
    } else {
      this.studioGenerateButtonTarget.innerHTML = "Run with Agent"
    }
  }
}
